import React, { Fragment, useContext, useCallback } from 'react';
import classnames from 'classnames';
import { Dropdown, Label, Stack, TextField } from '@bingads-webui-react/fabric-control';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import PropTypes from 'prop-types';
import { getAccountCurrencyCode, bidStrategyOptionKeys, Disclaimer } from '@bingads-webui-campaign/hotel-common-upsert';
import { ErrorString, SpanWithComponent, checkShouldShowError } from '@bingads-webui-campaign-react/wizard-shared-components';
import { validateBidStrategy, getPercentageOrBidErrorMsg, fields, bidStrategyOptions, validateAdGroupBid, getBidStrategyMinMax, getAdGroupBidMinMax } from './utility';

const Bid = React.memo((props) => {
  const {
    content,
    field,
    bidStrategy,
    setBidStrategy,
    isPercent,
    selectedKey,
  } = props;
  const serverContext = useContext(ServerContext);
  const accountCurrencyCode = getAccountCurrencyCode(serverContext);
  const i18n = useContext(I18nContext);
  const shouldShowError = checkShouldShowError(bidStrategy);
  const minMax = getAdGroupBidMinMax({ selectedKey, bidStrategy, serverContext });
  const error = getPercentageOrBidErrorMsg({
    code: validateAdGroupBid({ selectedKey, bidStrategy, ...minMax }),
    i18n,
    ...minMax,
    isPercent,
  });
  return (
    <div className="bid">
      <Label styles={{
        root: {
          marginTop: '8px',
          marginBottom: '8px',
        },
      }}
      >
        {content}
      </Label>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
        <TextField
          className={field}
          value={bidStrategy[field] || ''}
          placeholder=""
          ariaLabel={`${field}Label`}
          styles={{ fieldGroup: { width: 80 } }}
          onBlur={() => { setBidStrategy({ isDirty: true }); }}
          onChange={(e, value) => { setBidStrategy({ [field]: value, isDirty: true }); }}
        />
        {isPercent ? <span>{i18n.getString(_TL_('%'))}</span> : <span>{accountCurrencyCode}</span>}
      </Stack>
      {shouldShowError && error && <ErrorString error={error} />}
    </div>
  );
});

Bid.propTypes = {
  bidStrategy: PropTypes.shape({}),
  setBidStrategy: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  isPercent: PropTypes.bool.isRequired,
  selectedKey: PropTypes.string.isRequired,
};

Bid.defaultProps = {
  bidStrategy: {},
};

const Percentage = React.memo((props) => {
  const {
    content,
    field,
    bidStrategy,
    setBidStrategy,
    selectedKey,
    isPercent,
  } = props;
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const accountCurrencyCode = getAccountCurrencyCode(serverContext);
  const shouldShowError = checkShouldShowError(bidStrategy);
  const minMax = getBidStrategyMinMax({ selectedKey, bidStrategy });
  const error = getPercentageOrBidErrorMsg({
    code: validateBidStrategy({ selectedKey, bidStrategy, ...minMax }),
    i18n,
    ...minMax,
    isPercent,
  });

  return (
    <div className="percentage">
      <Label styles={{
        root: {
          marginBottom: '8px',
        },
      }}
      >
        {content}
      </Label>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
        <TextField
          ariaLabel={`${field}Label`}
          className={field}
          value={bidStrategy[field] || ''}
          placeholder=""
          styles={{ fieldGroup: { width: 80 } }}
          onBlur={() => { setBidStrategy({ isDirty: true }); }}
          onChange={(e, value) => { setBidStrategy({ [field]: value, isDirty: true }); }}
        />
        {isPercent ? <span>{i18n.getString(_TL_('%'))}</span> : <span>{accountCurrencyCode}</span>}
        {shouldShowError && error && <ErrorString error={error} />}
      </Stack>
    </div >
  );
});

Percentage.propTypes = {
  bidStrategy: PropTypes.shape({}),
  setBidStrategy: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  selectedKey: PropTypes.string.isRequired,
  isPercent: PropTypes.bool,
};

Percentage.defaultProps = {
  bidStrategy: {},
  isPercent: false,
};

const DefaultBidValue = React.memo((props) => {
  const {
    selectedKey,
  } = props;
  const i18n = useContext(I18nContext);
  switch (selectedKey) {
    case bidStrategyOptionKeys.ManualCpc:
      return (
        <Bid field={fields.AdGroupBid} content={i18n.getString(_TL_('Default ad group bid'))} {...props} selectedKey={selectedKey} />
      );
    case bidStrategyOptionKeys.PercentCpc:
      return (
        <Bid field={fields.AdGroupPercentBid} content={i18n.getString(_TL_('Default ad group percent bid'))} {...props} isPercent selectedKey={selectedKey} />
      );
    case bidStrategyOptionKeys.Commission:
    default:
      return null;
  }
});

DefaultBidValue.propTypes = {
  bidStrategy: PropTypes.shape({}),
  selectedKey: PropTypes.string.isRequired,
  setBidStrategy: PropTypes.func.isRequired,
};

DefaultBidValue.defaultProps = {
  bidStrategy: {},
};


const AdditionalInfo = React.memo((props) => {
  const {
    selectedKey,
    isPayPerStayEnabled,
  } = props;
  const i18n = useContext(I18nContext);
  switch (selectedKey) {
    case bidStrategyOptionKeys.ManualCpc:
      return null;
    case bidStrategyOptionKeys.PercentCpc:
      return (
        <Fragment>
          <Percentage field={fields.PercentCpc} content={i18n.getString(_TL_('Maximum effective bid limit'))} {...props} />
        </Fragment>
      );
    case bidStrategyOptionKeys.Commission: {
      let detail;
      if (isPayPerStayEnabled) {
        detail = (
          <Fragment>
            <Percentage field={fields.Commission} content={i18n.getString(_TL_('Room price (per stay)'))} isPercent {...props} />
          </Fragment>);
      } else {
        detail = null;
      }
      return detail;
    }
    default:
      return null;
  }
});

AdditionalInfo.propTypes = {
  bidStrategy: PropTypes.shape({}),
  selectedKey: PropTypes.string.isRequired,
  setBidStrategy: PropTypes.func.isRequired,
  isPayPerStayEnabled: PropTypes.bool.isRequired,
};

AdditionalInfo.defaultProps = {
  bidStrategy: {},
};

export const defaultBidStrategy = {
  option: bidStrategyOptionKeys.ManualCpc,
};

export const CampaignBidStrategy = React.memo((props) => {
  const {
    bidStrategy,
    setBidStrategy,
    scenario,
    getAdScheduleView,
    permissions,
  } = props;
  const isPayPerStayEnabled = permissions && permissions.dynamic && permissions.dynamic.PayPerStay;
  const mySetBidStrategy = useCallback((partialUpdate) => {
    const adScheduleView = getAdScheduleView();
    const data = {
      ...bidStrategy,
      ...partialUpdate,
    };
    if (adScheduleView != null) {
      if (data.option === bidStrategyOptionKeys.Commission) {
        adScheduleView.koModel.hideAdScheduleBidAdjustments(true);
      } else {
        adScheduleView.koModel.hideAdScheduleBidAdjustments(false);
      }
    }
    setBidStrategy(data);
  }, [bidStrategy, setBidStrategy, getAdScheduleView]);

  const isCreation = scenario === 'creation';
  const i18n = useContext(I18nContext);
  const howToManageYourBidsText = i18n.getString(_TL_('How do you want to manage your bids?'));
  const selectedKey = bidStrategy.option || bidStrategyOptionKeys.ManualCpc;
  const shouldShowError = checkShouldShowError(bidStrategy);
  const minMax = getBidStrategyMinMax({ selectedKey, bidStrategy });
  const errorCode = validateBidStrategy({ selectedKey, bidStrategy, ...minMax });

  return (
    <div className={classnames('campaign-bid-strategy', 'section')}>
      <div className="description">
        <Label
          className="campaign-bid-strategy-title-labe"
        >
          <SpanWithComponent
            SpanText={howToManageYourBidsText}
            Component={() => (
              <HelpPopup
                topic="pop_BA_CampaignGrids_BidStrategy"
                aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Bid Strategy')) })}
              />)}
          />
        </Label>
      </div>
      <div className="bid-strategy-options">
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <Dropdown
            label={i18n.getString(_TL_('Bid management'))}
            options={bidStrategyOptions(i18n, isPayPerStayEnabled)}
            defaultSelectedKey={selectedKey}
            styles={{
              dropdown: {
                width: 487,
                marginTop: '8px',
              },
            }}
            className="campaign-bid-strategy-dropdown"
            onChange={(e, option) => mySetBidStrategy({ option: option.key })}
          />
          <AdditionalInfo isPayPerStayEnabled={isPayPerStayEnabled} {...props} selectedKey={selectedKey} setBidStrategy={mySetBidStrategy} />
        </Stack>
        {isCreation && <DefaultBidValue {...props} selectedKey={selectedKey} setBidStrategy={mySetBidStrategy} />}
        {(selectedKey === bidStrategyOptionKeys.Commission && isPayPerStayEnabled) &&
          <div className={classnames('disclaimer', shouldShowError && errorCode !== null ? 'has-error' : 'has-no-error')}>
            <Disclaimer
              message={i18n.getString(_TL_('Not all criteria is applicable, only excluded locations & -100% devices bid adjustment is supported'))}
            />
          </div>
        }
      </div>
    </div>
  );
});

CampaignBidStrategy.propTypes = {
  bidStrategy: PropTypes.shape({}),
  setBidStrategy: PropTypes.func.isRequired,
  scenario: PropTypes.string.isRequired,
  getAdScheduleView: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PayPerStay: PropTypes.bool,
    }),
  }).isRequired,
};

CampaignBidStrategy.defaultProps = {
  bidStrategy: {},
};
